import { connect } from 'react-redux';
import React from 'react';
import { secondsToVerboseText } from '../../../../../utilities/timeUtilities';
import { createActions } from '../../store';
import { DEFAULT_AUDIO_TRACK } from '../../CreatePageConstants';
import { RealtimeAudioAnalyzer } from '../../Audio/RealtimeAudioAnalyzer';
import { getAudioVolume } from '../../../../../utilities/storageUtilities';
import ProgressSlider from './ProgressSlider';

class AudioPlaybackTimeline extends React.Component {
    state = {
        shouldResume: false,
        shouldRenewPause: false
    }

    _rafId = null;
    _sliderRef = React.createRef();
    _lastReduxSync = 0;

    render() {
        const { disabled, duration, appliedPlaybackTime } = this.props;
        const audioDuration = duration === 0 ? DEFAULT_AUDIO_TRACK.DURATION : duration;

        return (
            <ProgressSlider
                ref={this._sliderRef}
                max={audioDuration}
                onChange={this.onTimeSliderChange}
                onMouseDown={this.onTimeSliderDown}
                onChangeCommitted={this.onTimeSliderChangeCommitted}
                value={appliedPlaybackTime}
                disabled={disabled}
                aria-label='Preview playback progress'
                aria-valuetext={secondsToVerboseText(appliedPlaybackTime)}
            />
        );
    }

    componentDidMount() {
        // Start the rAF loop if already playing on mount
        if (this.props.playingPreview) {
            this._startAnimationLoop();
        }
    }

    componentDidUpdate(prevProps) {
        // Update the audio player position when dragging is finished
        if (prevProps.previewPositionDragging && !this.props.previewPositionDragging) {
            RealtimeAudioAnalyzer.setCurrentTime(this.props.appliedPlaybackTime);
        }

        // Start/stop the smooth animation loop based on playback state
        if (!prevProps.playingPreview && this.props.playingPreview) {
            this._startAnimationLoop();
        } else if (prevProps.playingPreview && !this.props.playingPreview) {
            this._stopAnimationLoop();
        }
    }

    componentWillUnmount() {
        this._stopAnimationLoop();
    }

    _startAnimationLoop = () => {
        this._stopAnimationLoop();
        this._lastReduxSync = 0;
        const tick = (timestamp) => {
            if (!this.props.previewPositionDragging) {
                const currentTime = RealtimeAudioAnalyzer.getCurrentTime();

                // Update the canvas slider directly — no React re-render
                const slider = this._sliderRef.current;
                if (slider) {
                    slider._targetValue = currentTime;
                }

                // Throttle Redux sync to ~10fps for other consumers
                if (timestamp - this._lastReduxSync >= 100) {
                    this._lastReduxSync = timestamp;
                    this.setAppliedPlaybackTime(currentTime);
                }
            }
            this._rafId = requestAnimationFrame(tick);
        };
        this._rafId = requestAnimationFrame(tick);
    }

    _stopAnimationLoop = () => {
        if (this._rafId != null) {
            cancelAnimationFrame(this._rafId);
            this._rafId = null;
        }
    }

    onTimeSliderDown = () => {
        const { playingPreview, setProperty  } = this.props;
        
        setProperty('previewPositionDragging', true);

        if (playingPreview) {
            setProperty('playingPreview', false);
            this.setState({ shouldResume: true });
        } else {
            this.setState({ shouldRenewPause: true });
        } 

        RealtimeAudioAnalyzer.setVolume(0);
        RealtimeAudioAnalyzer.play();
    }

    onTimeSliderChange = (event, time) => {
        const { appliedPlaybackTime } = this.props;

        if (Math.floor(time) !== Math.floor(appliedPlaybackTime)) 
            RealtimeAudioAnalyzer.setCurrentTime(time);

        if (RealtimeAudioAnalyzer.isPaused())                
            RealtimeAudioAnalyzer.play();
        
        this.setAppliedPlaybackTime(time);
    }

    onTimeSliderChangeCommitted = (event, time) => {
        const { setPlayingPreview, duration, audioMuted, setProperty } = this.props;
        const { shouldResume, shouldRenewPause } = this.state;

        this.setAppliedPlaybackTime(time);
        setProperty('previewPositionDragging', false);

        if (shouldResume) {
            setPlayingPreview(true);
            this.setState({shouldResume: false});
        }

        if (shouldRenewPause) {
            RealtimeAudioAnalyzer.pause();
            this.setState({ shouldRenewPause: false });
        }

        setTimeout(() => {
            if (duration !== DEFAULT_AUDIO_TRACK.DURATION && !audioMuted)
                RealtimeAudioAnalyzer.setVolume(getAudioVolume());
        }, 200)
    }

    setAppliedPlaybackTime = (time) => {
        const { setProperty } = this.props;
        setProperty('appliedPlaybackTime', time);
    }
}

const mapStateToProps = (state) => {
    const {
        appliedPlaybackTime,
        playingPreview,
        audioMuted,
        previewPositionDragging,
        audioDuration: duration
    } = state.create;

    return {
        duration,
        audioMuted,
        appliedPlaybackTime,
        playingPreview,
        previewPositionDragging
    }
};

const mapDispatchToProps = (dispatch) => {
    const setProperty = (key, value) => dispatch(createActions.setProperty(key, value));
    const setPlayingPreview = (playingPreview) => dispatch(createActions.setPlayingPreview(playingPreview));

    return {
        setProperty,
        setPlayingPreview
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(AudioPlaybackTimeline);